import React, { useContext, useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Select } from '../../../../catalyst-components/select';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface CheckinQuestion {
  id: string;
  type: "scale" | "text" | "choice";
  prompt: string;
  required: boolean;
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  choices?: string[];
}

interface CheckinInstruction {
  _id: Id<"checkin_instructions">;
  instruction?: string;
  greeting: string;
  frequency: number;
  questions?: CheckinQuestion[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

function createEmptyQuestion(): CheckinQuestion {
  return {
    id: generateId(),
    type: "scale",
    prompt: "",
    required: true,
    scale_min: 1,
    scale_max: 5,
    scale_min_label: "",
    scale_max_label: "",
  };
}

const QuestionEditor: React.FC<{
  question: CheckinQuestion;
  index: number;
  totalCount: number;
  onChange: (q: CheckinQuestion) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}> = ({ question, index, totalCount, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const addChoice = () => {
    onChange({ ...question, choices: [...(question.choices || []), ""] });
  };

  const updateChoice = (ci: number, value: string) => {
    const updated = [...(question.choices || [])];
    updated[ci] = value;
    onChange({ ...question, choices: updated });
  };

  const removeChoice = (ci: number) => {
    const updated = (question.choices || []).filter((_, i) => i !== ci);
    onChange({ ...question, choices: updated });
  };

  return (
    <div className="border border-border/50 rounded-lg p-4 bg-background/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-muted-foreground">Question {index + 1}</span>
        <div className="flex items-center gap-1">
          <Button type="button" plain disabled={index === 0} onClick={onMoveUp} className="p-1">
            <ChevronUpIcon className="h-4 w-4" />
          </Button>
          <Button type="button" plain disabled={index === totalCount - 1} onClick={onMoveDown} className="p-1">
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
          <Button type="button" plain onClick={onRemove} className="p-1 text-red-500">
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <Label>Type</Label>
            <Select
              value={question.type}
              onChange={(e) => {
                const type = e.target.value as CheckinQuestion["type"];
                const updated: CheckinQuestion = { ...question, type };
                if (type === "choice" && !updated.choices?.length) {
                  updated.choices = [""];
                }
                onChange(updated);
              }}
            >
              <option value="scale">Scale (1-5)</option>
              <option value="text">Free Text</option>
              <option value="choice">Multiple Choice</option>
            </Select>
          </Field>
          <Field>
            <Label>Required</Label>
            <Select
              value={question.required ? "yes" : "no"}
              onChange={(e) => onChange({ ...question, required: e.target.value === "yes" })}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </Field>
        </div>

        <Field>
          <Label>Question Text</Label>
          <Textarea
            rows={2}
            value={question.prompt}
            onChange={(e) => onChange({ ...question, prompt: e.target.value })}
            placeholder="e.g., How are you feeling about your job search?"
          />
        </Field>

        {question.type === "scale" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-cols-2 gap-2">
              <Field>
                <Label>Min</Label>
                <Input
                  type="number"
                  value={question.scale_min ?? 1}
                  onChange={(e) => onChange({ ...question, scale_min: parseInt(e.target.value) || 1 })}
                />
              </Field>
              <Field>
                <Label>Max</Label>
                <Input
                  type="number"
                  value={question.scale_max ?? 5}
                  onChange={(e) => onChange({ ...question, scale_max: parseInt(e.target.value) || 5 })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field>
                <Label>Min Label</Label>
                <Input
                  value={question.scale_min_label ?? ""}
                  onChange={(e) => onChange({ ...question, scale_min_label: e.target.value })}
                  placeholder="e.g., Not at all"
                />
              </Field>
              <Field>
                <Label>Max Label</Label>
                <Input
                  value={question.scale_max_label ?? ""}
                  onChange={(e) => onChange({ ...question, scale_max_label: e.target.value })}
                  placeholder="e.g., Extremely"
                />
              </Field>
            </div>
          </div>
        )}

        {question.type === "choice" && (
          <div>
            <span className="text-sm font-medium text-foreground">Choices</span>
            <div className="space-y-2 mt-1">
              {(question.choices || []).map((choice, ci) => (
                <div key={ci} className="flex gap-2">
                  <Input
                    value={choice}
                    onChange={(e) => updateChoice(ci, e.target.value)}
                    placeholder={`Choice ${ci + 1}`}
                  />
                  <Button type="button" plain onClick={() => removeChoice(ci)} className="text-red-500 shrink-0">
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" plain onClick={addChoice} className="text-sm text-primary">
                <PlusIcon className="h-4 w-4 mr-1 inline" />
                Add Choice
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const CheckinInstructions: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const instructions = useQuery(api.checkin_instructions.list) || [];
  const createInstruction = useMutation(api.checkin_instructions.create);
  const updateInstruction = useMutation(api.checkin_instructions.update);
  const deleteInstruction = useMutation(api.checkin_instructions.remove);
  const toggleActive = useMutation(api.checkin_instructions.toggleActive);

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    instruction?: CheckinInstruction;
  }>({ open: false });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<Id<"checkin_instructions"> | null>(null);
  const [formData, setFormData] = useState({
    greeting: '',
    frequency: 1,
    questions: [] as CheckinQuestion[],
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (editingId) {
      const instruction = instructions.find(i => i._id === editingId);
      if (instruction) {
        setFormData({
          greeting: instruction.greeting,
          frequency: instruction.frequency ?? (instruction as any).sessions_threshold ?? 1,
          questions: (instruction.questions as CheckinQuestion[]) || [],
        });
      }
    }
  }, [editingId, instructions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.greeting.trim()) {
      setAlertMessage('Greeting is required');
      setShowAlert(true);
      return;
    }

    if (formData.frequency < 1) {
      setAlertMessage('Frequency must be at least 1');
      setShowAlert(true);
      return;
    }

    if (formData.questions.length === 0) {
      setAlertMessage('At least one question is required');
      setShowAlert(true);
      return;
    }

    // Validate questions
    for (const q of formData.questions) {
      if (!q.prompt.trim()) {
        setAlertMessage('All questions must have text');
        setShowAlert(true);
        return;
      }
      if (q.type === "choice" && (!q.choices || q.choices.filter(c => c.trim()).length < 2)) {
        setAlertMessage('Multiple choice questions need at least 2 non-empty choices');
        setShowAlert(true);
        return;
      }
    }

    // Clean up questions before saving
    const cleanedQuestions = formData.questions.map(q => ({
      ...q,
      choices: q.type === "choice" ? q.choices?.filter(c => c.trim()) : undefined,
      scale_min: q.type === "scale" ? (q.scale_min ?? 1) : undefined,
      scale_max: q.type === "scale" ? (q.scale_max ?? 5) : undefined,
      scale_min_label: q.type === "scale" ? q.scale_min_label : undefined,
      scale_max_label: q.type === "scale" ? q.scale_max_label : undefined,
    }));

    try {
      if (editingId) {
        await updateInstruction({
          id: editingId,
          greeting: formData.greeting,
          frequency: formData.frequency,
          questions: cleanedQuestions,
          updated_by: convexUserId as Id<"users">,
        });
        setAlertMessage('Checkin updated successfully!');
      } else {
        await createInstruction({
          greeting: formData.greeting,
          frequency: formData.frequency,
          questions: cleanedQuestions,
          created_by: convexUserId as Id<"users">,
        });
        setAlertMessage('Checkin created successfully!');
      }
      setShowAlert(true);
      resetForm();
    } catch (error) {
      setAlertMessage('Error saving checkin. Please try again.');
      setShowAlert(true);
    }
  };

  const resetForm = () => {
    setFormData({ greeting: '', frequency: 1, questions: [] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (instruction: CheckinInstruction) => {
    setEditingId(instruction._id);
    setShowForm(true);
  };

  const handleToggleActive = async (id: Id<"checkin_instructions">) => {
    try {
      await toggleActive({
        id,
        updated_by: convexUserId as Id<"users">,
      });
    } catch (error) {
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, createEmptyQuestion()],
    });
  };

  const updateQuestion = (index: number, q: CheckinQuestion) => {
    const updated = [...formData.questions];
    updated[index] = q;
    setFormData({ ...formData, questions: updated });
  };

  const removeQuestion = (index: number) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index),
    });
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.questions.length) return;
    const updated = [...formData.questions];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFormData({ ...formData, questions: updated });
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-orange-50 to-orange-100/50 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button
              color="secondary"
              className="rounded-full"
              onClick={() => navigate('../')}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-white border-2 border-orange-200">
                <CheckCircleIcon className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-1">Check-in Forms</h1>
                <p className="text-base text-muted-foreground">
                  Configure check-in forms with questions for users
                </p>
              </div>
            </div>
            <Button
              color="primary"
              className="rounded-full"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add New Checkin
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-6">
          {showForm && (
            <div className="border-2 border-orange-200 bg-gradient-to-br from-orange-50/50 to-orange-100/30 p-6 rounded-xl">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {editingId ? 'Edit Checkin' : 'New Checkin'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Field>
                  <Label>Frequency (every N sessions)</Label>
                  <Input
                    required
                    type="number"
                    min={1}
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Show this checkin every time the user completes this many sessions
                  </p>
                </Field>

                <Field>
                  <Label>Greeting Message</Label>
                  <Textarea
                    required
                    rows={3}
                    value={formData.greeting}
                    onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                    placeholder="Enter the greeting message shown at the top of the check-in modal..."
                  />
                </Field>

                {/* Questions Builder */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-foreground">Questions</span>
                    <Button type="button" color="secondary" className="rounded-full text-sm" onClick={addQuestion}>
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Question
                    </Button>
                  </div>
                  {formData.questions.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic py-4 text-center border border-dashed border-border/50 rounded-lg">
                      No questions yet. Click "Add Question" to get started.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {formData.questions.map((q, i) => (
                        <QuestionEditor
                          key={q.id}
                          question={q}
                          index={i}
                          totalCount={formData.questions.length}
                          onChange={(updated) => updateQuestion(i, updated)}
                          onRemove={() => removeQuestion(i)}
                          onMoveUp={() => moveQuestion(i, -1)}
                          onMoveDown={() => moveQuestion(i, 1)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button type="submit" color="primary" className="rounded-full">
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" color="secondary" className="rounded-full" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
            <Table>
              <TableHead>
                <TableRow className="bg-muted/30">
                  <TableHeader>Frequency</TableHeader>
                  <TableHeader>Greeting</TableHeader>
                  <TableHeader>Questions</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {instructions.map((instruction) => (
                  <TableRow key={instruction._id} className="hover:bg-muted/20">
                    <TableCell className="font-semibold text-foreground">
                      Every {instruction.frequency ?? (instruction as any).sessions_threshold ?? 0} session{(instruction.frequency ?? (instruction as any).sessions_threshold ?? 0) !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {instruction.greeting}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {(instruction as any).questions?.length ?? 0} question{(instruction as any).questions?.length !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell>
                      {instruction.is_active ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                          <CheckCircleIcon className="h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground bg-muted/30 border border-border/50 rounded-full">
                          <XCircleIcon className="h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          color="secondary"
                          className="rounded-full"
                          onClick={() => handleEdit(instruction)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          color={instruction.is_active ? "secondary" : "primary"}
                          className="rounded-full"
                          onClick={() => handleToggleActive(instruction._id)}
                        >
                          {instruction.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          color="red"
                          className="rounded-full"
                          onClick={() => setConfirmDelete({ open: true, instruction })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {instructions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No checkin instructions found. Add one to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={async () => {
          if (!confirmDelete.instruction) return;
          try {
            await deleteInstruction({ id: confirmDelete.instruction._id });
            setConfirmDelete({ open: false });
          } catch (error) {
          }
        }}
        title="Delete Checkin"
        description="Are you sure you want to delete this checkin? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />

      <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowAlert(false)}>
            Ok
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
